import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  Alert,
} from '@mui/material';
import { IconUserOff } from '@tabler/icons-react';
import { useState } from 'react';
import Functions from '../../../../callable/functions';
import Usuario from '../../../../models/usuario';
import { useSnackBar } from '../../../../stores/snackBarStores';
import { FirebaseError } from 'firebase/app';
import moment from 'moment';

type Props = {
  cliente: Usuario;
  open: boolean;
  onClose: (success: boolean) => void;
};
export default function ExcluirClienteDialog({
  cliente,
  open,
  onClose,
}: Props) {
  const [excluindo, setExcluindo] = useState(false);
  const snackBar = useSnackBar();

  // Verifica se o cliente pode ser arquivado/desvinculado (regra de 30 dias)
  function podeArquivar(): { permitido: boolean; dataLiberacao?: string } {
    if (!cliente.atualizadoEm) {
      return { permitido: true }; // Nunca teve cálculo
    }

    const limite = moment().subtract(1, 'month');
    if (cliente.atualizadoEm.isSameOrBefore(limite)) {
      return { permitido: true }; // Já passou de 30 dias
    }

    // Ainda está no prazo de carência
    return {
      permitido: false,
      dataLiberacao: cliente.atualizadoEm
        .clone()
        .add(1, 'month')
        .format('DD/MM'),
    };
  }

  const statusArquivamento = podeArquivar();

  if (!!cliente.email) {
    return (
      <Dialog open={open} maxWidth="xs" fullWidth>
        <DialogTitle color="text.primary">
          <Box display="flex" flexDirection="row" alignItems="center">
            <Box mr={1}>
              <IconUserOff size={16} />
            </Box>
            <Typography variant="h4">
              Remover {cliente.nome}?
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {cliente.nome} ainda terá acesso à Felix através do email{' '}
            {cliente.email}, mas ninguém da sua organização poderá vê-lo.
          </DialogContentText>
          {!statusArquivamento.permitido && (
            <Box mt={2}>
              <Alert severity="warning">
                Este cliente só poderá ser removido a partir de{' '}
                <Typography
                  component="strong"
                  variant="dataMono"
                  fontWeight={600}
                >
                  {statusArquivamento.dataLiberacao}
                </Typography>
                , pois o último cálculo foi há menos de 30 dias.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => onClose(false)} color="inherit">
            {statusArquivamento.permitido ? 'Cancelar' : 'Fechar'}
          </Button>
          {statusArquivamento.permitido && (
            <LoadingButton
              color="error"
              variant="contained"
              loading={excluindo}
              onClick={() => {
                setExcluindo(true);
                new Functions()
                  .desvincularCliente({ clienteId: cliente.id })
                  .then(() => {
                    snackBar({
                      type: 'ALERT',
                      payload: {
                        mensagem: `O cliente ${cliente.nome} foi removido.`,
                        severidade: 'success',
                      },
                    });
                  })
                  .catch((error: FirebaseError) => {
                    snackBar({
                      type: 'ALERT',
                      payload: {
                        mensagem: error.message,
                        severidade: 'error',
                      },
                    });
                  })
                  .finally(() => {
                    setExcluindo(false);
                    onClose(true);
                  });
              }}
            >
              Remover
            </LoadingButton>
          )}
        </DialogActions>
      </Dialog>
    );
  } else {
    return (
      <Dialog open={open}>
        <DialogTitle color="text.primary">
          <Box display="flex" flexDirection="row" alignItems="center">
            <Box mr={1}>
              <IconUserOff size={16} />
            </Box>
            <Typography variant="h4">
              Arquivar {cliente.nome}?
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            O cliente não será apagado, mas ninguém do seu escritório conseguirá
            acessá-lo.
          </DialogContentText>
          {!statusArquivamento.permitido && (
            <Box mt={2}>
              <Alert severity="warning">
                Este cliente só poderá ser arquivado a partir de{' '}
                <Typography
                  component="strong"
                  variant="dataMono"
                  fontWeight={600}
                >
                  {statusArquivamento.dataLiberacao}
                </Typography>
                , pois o último cálculo foi há menos de 30 dias.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => onClose(false)} color="inherit">
            {statusArquivamento.permitido ? 'Cancelar' : 'Fechar'}
          </Button>
          {statusArquivamento.permitido && (
            <LoadingButton
              color="error"
              variant="contained"
              loading={excluindo}
              onClick={() => {
                setExcluindo(true);
                new Functions()
                  .arquivarCliente({ clienteId: cliente.id })

                  .then(() => {
                    snackBar({
                      type: 'ALERT',
                      payload: {
                        mensagem: `O cliente ${cliente.nome} foi arquivado.`,
                        severidade: 'success',
                      },
                    });
                  })
                  .catch((error: FirebaseError) => {
                    snackBar({
                      type: 'ALERT',
                      payload: {
                        mensagem: error.message.replace('Error: ', ''),
                        severidade: 'error',
                      },
                    });
                  })
                  .finally(() => {
                    setExcluindo(false);
                    onClose(true);
                  });
              }}
            >
              Arquivar
            </LoadingButton>
          )}
        </DialogActions>
      </Dialog>
    );
  }
}
